import { useState, useRef } from 'react';
import { ProjectDto, useBulkUpdateSpoolsMutation } from 'providers/api';
import { invalidate } from '@react-three/fiber';
import PCSpool from '../../MineModel/Spools/PCSpool';
import { Model } from '../../hooks/useModelState';

type DirectionMode = 'FORWARDS' | 'BACKWARDS' | 'BIDIRECTIONAL';

interface FlowFlags {
  allowFlowAB: boolean;
  allowFlowBA: boolean;
}

interface UseChangeFlowDirectionProps {
  model: Model;
  project: ProjectDto;
}

const flagsForMode = (mode: DirectionMode): FlowFlags => {
  if (mode === 'BIDIRECTIONAL') return { allowFlowAB: true, allowFlowBA: true };
  if (mode === 'FORWARDS') return { allowFlowAB: true, allowFlowBA: false };
  return { allowFlowAB: false, allowFlowBA: true };
};

const useChangeFlowDirection = ({ project, model }: UseChangeFlowDirectionProps) => {
  const [isSelectingSpools, setIsSelectingSpools] = useState(false);
  const [directionMode, setDirectionMode] = useState<DirectionMode | null>(null);
  const [selectedSpool, setSelectedSpool] = useState<PCSpool | null>(null);
  const previousFlowFlagsRef = useRef<FlowFlags | null>(null);

  const bulkUpdateSpoolsMutation = useBulkUpdateSpoolsMutation(project.entityId);

  // Flow direction is a property of the spool and is patched on its own id — no pair-derived id, no
  // node round trip. The spool's flags stay authoritative; geometry only ever seeds them at creation.
  const handleSpoolSelect = (spool: PCSpool | undefined) => {
    if (!isSelectingSpools || !directionMode || !model || !spool) return;

    const spoolInModel = model.spools[spool.spoolId];
    if (!spoolInModel) return;

    previousFlowFlagsRef.current = {
      allowFlowAB: spoolInModel.allowFlowAB,
      allowFlowBA: spoolInModel.allowFlowBA,
    };

    const flags = flagsForMode(directionMode);
    model.setSpoolProperties(spoolInModel.spoolId, flags);
    invalidate();

    bulkUpdateSpoolsMutation.mutate(
      { projectId: project.entityId, spools: [{ spoolId: spoolInModel.spoolId, ...flags }] },
      {
        onError: () => {
          if (!previousFlowFlagsRef.current) return;
          model.setSpoolProperties(spoolInModel.spoolId, previousFlowFlagsRef.current);
          invalidate();
        },
      },
    );
  };

  const handleSelectForDirection = (mode: DirectionMode) => {
    if (isSelectingSpools && directionMode === mode) {
      setIsSelectingSpools(false);
      setDirectionMode(null);
      setSelectedSpool(null);
      previousFlowFlagsRef.current = null;
      return;
    }
    setDirectionMode(mode);
    setIsSelectingSpools(true);
    setSelectedSpool(null);
    previousFlowFlagsRef.current = null;
  };

  const handleCancelSelection = () => {
    setIsSelectingSpools(false);
    setDirectionMode(null);
    setSelectedSpool(null);
    previousFlowFlagsRef.current = null;
  };

  return {
    isSelectingNodes: isSelectingSpools,
    directionMode,
    selectedNode: selectedSpool,
    handleSelectForDirection,
    handleCancelSelection,
    handleSpoolSelect,
  };
};

export default useChangeFlowDirection;
